import { ApiClient } from './api-client';
import type { ApiResponse } from '../types';

export type PostCategory =
  | 'BUSINESS'
  | 'MONEY'
  | 'AI'
  | 'FREELANCING'
  | 'CAREER'
  | 'MOTIVATION'
  | 'INVESTING'
  | 'PRODUCTIVITY'
  | 'STARTUPS'
  | 'TECHNOLOGY';

/** Fixed list — keep in lockstep with the backend `PostCategory` enum and the
 *  `opportunities.category.*` i18n keys. */
export const POST_CATEGORIES: PostCategory[] = [
  'BUSINESS',
  'MONEY',
  'AI',
  'FREELANCING',
  'CAREER',
  'MOTIVATION',
  'INVESTING',
  'PRODUCTIVITY',
  'STARTUPS',
  'TECHNOLOGY',
];

export interface AuthorRef {
  userId: string;
  name: string;
  /** Null until the User entity exposes one — components must fall back to initials. */
  avatarUrl: string | null;
}

export interface Post {
  id: string;
  author: AuthorRef;
  title: string;
  body: string;
  category: PostCategory;
  imageThumbUrl: string | null;
  imageMediumUrl: string | null;
  imageLargeUrl: string | null;
  likeCount: number;
  saveCount: number;
  commentCount: number;
  likedByMe: boolean;
  savedByMe: boolean;
  /** ISO-8601 UTC instant. */
  createdAt: string;
}

// ── Phase 2 — write payloads ─────────────────────────────────────────────

/** Shape posted to `POST /opportunities/posts`. Image URLs are obtained
 *  from a prior {@link uploadPostImage} call — pass all three, or omit
 *  them entirely. */
export interface CreatePostBody {
  title: string;
  body: string;
  category: PostCategory;
  imageThumbUrl?: string | null;
  imageMediumUrl?: string | null;
  imageLargeUrl?: string | null;
}

/** Same shape as create — the backend overwrites the four user-visible
 *  fields wholesale. Sending three null image URLs clears the image. */
export type UpdatePostBody = CreatePostBody;

export interface ReportPostBody {
  reason: string;
}

/** Returned by `POST /uploads/portfolio` — three WebP variants plus the
 *  random-UUID folder. Mirrors {@code UploadedImage} in providers.service
 *  to avoid a cross-module import. */
export interface UploadedPostImage {
  url: string;
  thumbUrl: string;
  mediumUrl: string;
  largeUrl: string;
  folder: string;
  sizeBytes: number;
}

/** Response of `POST /opportunities/posts/{id}/like`. */
export interface LikeToggleResult {
  liked: boolean;
  likeCount: number;
}

/** Response of `POST /opportunities/posts/{id}/save`. */
export interface SaveToggleResult {
  saved: boolean;
  saveCount: number;
}

/** One comment as returned by the listing + create endpoints. */
export interface Comment {
  id: string;
  postId: string;
  author: AuthorRef;
  body: string;
  /** ISO-8601 UTC instant. */
  createdAt: string;
  /** True when the viewer is the comment's author OR an admin — drives the
   *  delete affordance without a second auth check. */
  canDelete: boolean;
}

export interface CreateCommentBody {
  body: string;
}

export const opportunitiesService = {
  /** Paginated feed. Backend caps `size` at 50 — anything above is clamped. */
  list: (params: {
    category?: PostCategory;
    page?: number;
    size?: number;
  } = {}): Promise<ApiResponse<Post[]>> =>
    ApiClient.get<Post[]>('/opportunities/posts', {
      ...(params.category ? { category: params.category } : {}),
      page: params.page ?? 0,
      size: params.size ?? 20,
    }),

  /** Fetch a single post by id. Throws when hidden or missing (backend → 404). */
  get: (id: string): Promise<ApiResponse<Post>> =>
    ApiClient.get<Post>(`/opportunities/posts/${id}`),

  // ── Phase 2 — author writes ──────────────────────────────────────────

  /** Create a new post. Backend enforces a per-user 5/day rate limit and
   *  rejects with BUSINESS_RULE_VIOLATION beyond that. */
  create: (body: CreatePostBody): Promise<ApiResponse<Post>> =>
    ApiClient.post<Post>('/opportunities/posts', body),

  /** Edit one of my posts. Backend enforces a 15-minute window after
   *  creation; later edits return BUSINESS_RULE_VIOLATION. */
  update: (id: string, body: UpdatePostBody): Promise<ApiResponse<Post>> =>
    ApiClient.patch<Post>(`/opportunities/posts/${id}`, body),

  /** Hard-delete one of my posts. The DB cascade removes related reports. */
  remove: (id: string): Promise<void> =>
    ApiClient.delete(`/opportunities/posts/${id}`).then(() => undefined),

  /** Flag a post for admin review. Idempotent — a second click from the
   *  same user is silently accepted. */
  report: (id: string, body: ReportPostBody): Promise<void> =>
    ApiClient.post(`/opportunities/posts/${id}/report`, body).then(() => undefined),

  // ── Phase 3 — engagement ─────────────────────────────────────────────

  /** Toggle a like. Backend returns the new state + updated count so the
   *  UI can paint without re-fetching the post. */
  toggleLike: (id: string): Promise<ApiResponse<LikeToggleResult>> =>
    ApiClient.post<LikeToggleResult>(`/opportunities/posts/${id}/like`),

  /** Toggle a save (bookmark). */
  toggleSave: (id: string): Promise<ApiResponse<SaveToggleResult>> =>
    ApiClient.post<SaveToggleResult>(`/opportunities/posts/${id}/save`),

  /** My saved posts, newest-saved first. Auth-required on the backend. */
  listSaved: (params: { page?: number; size?: number } = {}): Promise<ApiResponse<Post[]>> =>
    ApiClient.get<Post[]>('/opportunities/me/saved', {
      page: params.page ?? 0,
      size: params.size ?? 20,
    }),

  // ── Phase 4 — comments ───────────────────────────────────────────────

  /** Paginated thread of comments for a post, oldest-first. Public read. */
  listComments: (
    postId: string,
    params: { page?: number; size?: number } = {},
  ): Promise<ApiResponse<Comment[]>> =>
    ApiClient.get<Comment[]>(`/opportunities/posts/${postId}/comments`, {
      page: params.page ?? 0,
      size: params.size ?? 20,
    }),

  /** Post a new comment on a post. Auth-required. */
  createComment: (postId: string, body: CreateCommentBody): Promise<ApiResponse<Comment>> =>
    ApiClient.post<Comment>(`/opportunities/posts/${postId}/comments`, body),

  /** Delete a comment. Backend allows the author or any admin. */
  deleteComment: (commentId: string): Promise<void> =>
    ApiClient.delete(`/opportunities/comments/${commentId}`).then(() => undefined),

  /** Upload a cover image via the existing portfolio endpoint. The three
   *  returned URLs feed straight into {@link create} / {@link update}. */
  uploadImage: async (
    file: File,
    onProgress?: (percent: number) => void,
  ): Promise<UploadedPostImage> => {
    const form = new FormData();
    form.append('file', file);
    return (await ApiClient.postMultipart<UploadedPostImage>(
      '/uploads/portfolio',
      form,
      onProgress,
    )).data;
  },
};
